import {
  Box,
  Button,
  Flex,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Text,
} from '@chakra-ui/react';
import { FiSearch } from 'react-icons/fi/index.js';
import { X } from 'lucide-react';
import { useCallback, useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';

type NATColumnFilterPopoverProps = {
  columnName: string;
  onApply: (value: string) => void;
  onCancel: () => void;
};

export function NATColumnFilterPopover({
  columnName,
  onApply,
  onCancel,
}: NATColumnFilterPopoverProps) {
  const [draft, setDraft] = useState('');

  const handleApply = useCallback(() => {
    const trimmed = draft.trim();
    if (trimmed !== '') {
      onApply(trimmed);
    }
  }, [draft, onApply]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        handleApply();
      }
    },
    [handleApply],
  );

  return (
    <Popover placement="left-start" isOpen={!!columnName} onClose={onCancel}>
      <PopoverTrigger>
        <Box display="inline-block" />
      </PopoverTrigger>
      <PopoverContent zIndex="popover">
        <PopoverArrow />
        <PopoverCloseButton />
        <PopoverHeader>
          <Text>
            <Trans>Filter by</Trans> {columnName}
          </Text>
        </PopoverHeader>
        <PopoverBody>
          <Flex direction="column" gap={2}>
            <InputGroup maxW="xs" size="sm">
              <InputLeftElement pointerEvents="none" mt="1px">
                <Icon as={FiSearch} color="fg.subtle" fontSize="lg" />
              </InputLeftElement>
              <Input
                autoFocus
                autoComplete="off"
                size="sm"
                type="text"
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={handleKeyDown}
                width="full"
                fontSize="sm"
              />
              {draft !== '' ? (
                <InputRightElement height="100%">
                  <Box
                    as="button"
                    type="button"
                    bg="bg.emphasis"
                    borderRadius="md"
                    px={1}
                    cursor="pointer"
                    onClick={() => setDraft('')}
                    aria-label={t`Clear`}
                  >
                    <Icon as={X} color="fg.muted" fontSize="md" />
                  </Box>
                </InputRightElement>
              ) : null}
            </InputGroup>
            <Flex gap={2}>
              <Button size="sm" variant="outline" flex="1" onClick={onCancel}>
                <Trans>Cancel</Trans>
              </Button>
              <Button size="sm" flex="1" onClick={handleApply} isDisabled={draft.trim() === ''}>
                <Trans>Apply</Trans>
              </Button>
            </Flex>
          </Flex>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}
